import i18n from "i18next";

import { openDatabase } from "../Utils/db";

import type { ISceneDB } from "../interfaces/Scenes";

import {
  SCENE_THUMB_HEIGHT,
  SCENE_THUMB_WIDTH,
  canvasToJpegBytes,
  writeSceneThumbnail,
} from "./scenes/seedSceneThumbnails";

import type { StingerConfig } from "./LivePreview/Strategy/SceneTransition";

import GlobalAudio from "./AudioStream";

import BroadcastManager from "./BroadcastManager";

import Cameras from "./Devices/Cameras";

import Microphones from "./Devices/Microphones";

import Playlist from "./Devices/Playlist";

import LivePreview from "./LivePreview/LivePreview";

import SceneInitializer from "./SceneInitializer";

import PanelWidgetInitializer from "./PanelWidgetInitializer";

import ScenesManager from "./ScenesManager";

import Team from "./Team";

import type { IStudio } from "../screens/Dashboard/SubComponents/Studio/interfaces/Studio.interface";

import Streaming from "./Streaming";

import type { BroadcastConnectionStats } from "../broadcast/sceneSync";

import SystemAudioCapture from "./SystemAudioCapture";

import { IStudioArea } from "../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Areas.interface";

import { ITeamDeviceDB } from "../interfaces/Team";

import CurrentWorkspace from "./Workspace";

import CurrentUser from "./User";

import SceneManager from "./Managers/SceneManager";

import TriggerEngine from "./TriggerEngine";

import GuestAudioManager from "./Managers/GuestAudioManager";

import {
  isBroadcastWindow,
  startOsrBroadcastFromControl,
  stopOsrBroadcastFromControl,
} from "../broadcast/controlBroadcast";

export enum States {
  unInitialized = "unInitialized",

  preInitialize = "preInitialize",

  initialized = "initialized",

  playing = "playing",

  destroyed = "destroyed",
}

export let RadioInstance: null | Radio = null;

class Radio {
  Microphones!: Microphones;

  Cameras!: Cameras;

  Playlist!: Playlist;

  Streaming!: Streaming;

  Broadcast!: BroadcastManager;

  Team!: Team;

  ScenesManager!: ScenesManager;

  LivePreview!: LivePreview;

  GlobalAudio!: GlobalAudio;

  SystemAudio!: SystemAudioCapture;

  Triggers!: TriggerEngine;

  private State = States.unInitialized;

  constructor() {
    if (RadioInstance) return RadioInstance;

    RadioInstance = this; // eslint-disable-line @typescript-eslint/no-this-alias -- singleton pattern requires this-alias

    this.createInstance();

    return this;
  }

  private async createInstance() {
    if (
      this?.GlobalAudio?.AudioStreamContext ||
      this.State !== States.unInitialized
    )
      return;

    this.State = States.preInitialize;

    this.GlobalAudio = new GlobalAudio();

    this.Microphones = new Microphones(
      this.GlobalAudio.AudioStreamContext,

      this.GlobalAudio.AudioStreamDestination,
    );

    this.Playlist = new Playlist(
      this.GlobalAudio.AudioStreamContext,

      this.GlobalAudio.AudioStreamDestination,
    );

    this.Cameras = new Cameras();

    this.ScenesManager = new ScenesManager();

    this.Team = new Team();

    this.Streaming = new Streaming();

    this.Broadcast = new BroadcastManager(this.Streaming);

    this.SystemAudio = new SystemAudioCapture(
      this.GlobalAudio.AudioStreamContext,

      this.GlobalAudio.AudioStreamDestination,
    );

    this.Triggers = new TriggerEngine(this);
  }

  isInitialized() {
    return this.State === States.initialized;
  }

  async initialize() {
    if (this.State === States.initialized) return;

    // initConfigurations es solo Dexie + i18n: corre en paralelo con las capturas. Las capturas de
    // media (audio del sistema y team de dispositivos) van EN SERIE a propósito: ambas hacen
    // getUserMedia/getDisplayMedia y solaparlas puede mezclar prompts de permisos (macOS/TCC) o
    // fallar por carreras en un primer arranque. initScenes depende del team (las cámaras del
    // modelo se resuelven contra el mapa de Cameras).
    const configurations = this.initConfigurations();

    // Marca la promesa como manejada mientras esperamos las capturas (sin esto un fallo temprano
    // dispararía unhandledrejection); el error real se propaga igual en el await de abajo.
    configurations.catch(() => {});

    await this.initGlobalAudio();

    await this.initializeTeam();

    await configurations;

    await this.initScenes();

    // Paneles del Dashboard y reglas de triggers son cosas del CONTROL (UI del operador). El OSR
    // window solo renderiza y emite la escena que le publica el control: sembrar paneles o cablear
    // fuentes de eventos ahí es trabajo muerto que alarga el arranque de cada emisión.
    if (!isBroadcastWindow()) {
      await Promise.all([this.initPanelWidgets(), this.Triggers.sync()]);
    }

    this.State = States.initialized;
  }

  private async initGlobalAudio() {
    await this.SystemAudio.startCapture();
  }

  // Hot-plug de mics (gap visto en mac, aplica a todas las plataformas): sin

  // listener de devicechange, enchufar un micrófono después del boot no

  // re-enumera y la UI queda en "No microphones connected" hasta reiniciar.

  private devicechangeReseedRegistered = false;

  private devicechangeHandler: (() => void) | null = null;

  private reseedDebounce: ReturnType<typeof setTimeout> | null = null;

  private reseedInFlight = false;

  private registerDeviceChangeReseed() {
    if (this.devicechangeReseedRegistered) return;

    this.devicechangeReseedRegistered = true;

    // Debounce: devicechange se emite en ráfagas (un solo enchufe dispara

    // varios eventos) — sin esto correrían reseeds en paralelo con múltiples

    // getUserMedia y escrituras concurrentes al team. El handler queda

    // referenciado para removerlo en destroy() (si no, cada ciclo

    // destroy/recreate del singleton acumularía listeners).

    this.devicechangeHandler = () => {
      if (this.reseedDebounce) clearTimeout(this.reseedDebounce);

      this.reseedDebounce = setTimeout(() => {
        this.reseedDebounce = null;

        void this.reseedMicsIfMissing();
      }, 500);
    };

    navigator.mediaDevices?.addEventListener?.(
      "devicechange",

      this.devicechangeHandler,
    );
  }

  private unregisterDeviceChangeReseed() {
    if (this.devicechangeHandler) {
      navigator.mediaDevices?.removeEventListener?.(
        "devicechange",

        this.devicechangeHandler,
      );

      this.devicechangeHandler = null;
    }

    if (this.reseedDebounce) {
      clearTimeout(this.reseedDebounce);

      this.reseedDebounce = null;
    }

    this.devicechangeReseedRegistered = false;
  }

  // Re-siembra el default si el team persistido no tiene ningún mic USABLE

  // (deviceInfo.deviceId, mismo criterio con el que filtra Microphones.tsx).

  private async reseedMicsIfMissing() {
    // Guard de concurrencia: un reseed a la vez (getDefaultTeam hace

    // getUserMedia y setTeam escribe la DB — no deben solaparse).

    if (this.reseedInFlight) return;

    this.reseedInFlight = true;

    try {
      const team = await this.Team.getTeam();

      if (!team) return;

      const hasUsableMic = (team.mics ?? []).some(
        (mic) => !!mic?.deviceInfo?.deviceId,
      );

      if (hasUsableMic) return;

      const seeded = await this.Team.getDefaultTeam();

      if (seeded.mics.length > 0) {
        for (const mic of seeded.mics) {
          this.Microphones.set(mic.deviceInfo.deviceId, mic);
        }

        // Sin postMessage("team.loaded"): la tira de mics (Microphones.tsx) usa
        // Dexie useLiveQuery y se refresca sola con esta escritura; team.loaded
        // dispararía además refresh de cámaras en LivePreview, innecesario para
        // un reseed que solo toca mics.
        await this.Team.setTeam({ ...team, mics: seeded.mics });
      }
    } catch (error) {
      // Best-effort (se dispara desde devicechange): nunca propagar — un fallo
      // de DB/getUserMedia acá no debe generar unhandled rejection.
      console.warn("[Radio] reseed de mics falló:", error);
    } finally {
      this.reseedInFlight = false;
    }
  }

  private async initializeTeam() {
    this.registerDeviceChangeReseed();

    const team = await this.Team.getTeam();

    if (team) {
      // Solo cargamos mics VÁLIDOS (con deviceInfo.deviceId). Un mic malformado

      // (p.ej. `{}` u objeto sin deviceInfo, persistido por un onboarding/boot

      // viejo) no debe entrar al mapa: la UI lo filtra igual y haría un

      // set(undefined, {}). El re-seed de abajo se encarga de reemplazarlo.

      for (const mic of team.mics) {
        if (mic?.deviceInfo?.deviceId) {
          this.Microphones.set(mic.deviceInfo.deviceId, mic);
        }
      }

      for (const cam of team.cams) {
        this.Cameras.set(cam.id, cam);
      }

      // Auto-recuperación: si el team persistido no tiene NINGÚN mic USABLE

      // re-sembramos el default (mismo helper que usa el listener devicechange;

      // reemplaza mics malformados sin tocar cams).

      await this.reseedMicsIfMissing();

      setTimeout(() => {
        window.postMessage({ type: "team.loaded" });
      }, 2000);

      return;
    }

    const defaultTeam = await this.Team.getDefaultTeam();

    if (defaultTeam) {
      for (const mic of defaultTeam.mics) {
        this.Microphones.set(mic.deviceInfo.deviceId, mic);
      }
    }

    for (const cam of defaultTeam.cams) {
      this.Cameras.set(cam.deviceInfo.deviceId, cam);
    }

    await this.Team.setTeam(defaultTeam);

    setTimeout(() => {
      window.postMessage({ type: "team.loaded" });
    }, 2000);
  }

  getState() {
    return this.State;
  }

  private async initScenes() {
    const initializer = new SceneInitializer(this.ScenesManager, this.Cameras);

    await initializer.initialize();
  }

  private async initPanelWidgets() {
    const initializer = new PanelWidgetInitializer();

    await initializer.initialize();
  }

  async startScene() {
    const model = this.ScenesManager.getModel();

    if (!model) return;

    this.LivePreview = new LivePreview({ type: model.type, model });

    this.LivePreview.setAudioContext(
      this.GlobalAudio.AudioStreamContext,

      this.GlobalAudio.AudioStreamDestination,
    );

    this.LivePreview.on(
      "ended",

      this.ScenesManager.nextModel.bind(this.ScenesManager),
    );

    try {
      await this.LivePreview.play();

      await this.startPlaylist();

      this.State = States.playing;
    } catch (error) {
      console.error("Error playing video:", error);
    }
  }

  async updateScene(scene: ISceneDB) {
    await this.ScenesManager.setModel(scene.model);

    if (!this.LivePreview) return;

    try {
      await Promise.race([
        this.LivePreview.updateScene(scene),

        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error("LivePreview.updateScene timeout")),

            3000,
          ),
        ),
      ]);
    } catch {
      // LivePreview can hang after destroyPlayers — don't block the caller
    }
  }

  async updateScreenshot(path: string) {
    await this.Broadcast.updateScreenshot(path);
  }

  stopScene() {
    if (!this.LivePreview) return;

    this.LivePreview.stopScene();
  }

  resumeScene() {
    if (!this.LivePreview) return;

    this.LivePreview.resumeScene();
  }

  async switchScene(scene: ISceneDB) {
    if (!this.LivePreview) return;

    await this.LivePreview.switchScene(scene, this.Broadcast.isStreaming());
  }

  /**
   * Pre-construye una escena fuera de pantalla (pool keep-alive) para que el
   * próximo pase a ella sea instantáneo. Se llama on-intent (hover/armado).
   */

  async prewarmScene(scene: ISceneDB) {
    if (!this.LivePreview) return;

    await this.LivePreview.prewarmScene(scene);
  }

  /**
   * Miniatura de una escena NO activa para la tira de escenas: render fuera de pantalla +
   * `${workspace}/scenes/<model.id>.jpg` (el mismo archivo que escribe la captura de la escena
   * activa). Sin esto, las escenas sin captura (presets locales, sembrado fallido, workspace
   * importado) salían con el ícono de imagen rota hasta que el usuario las activaba.
   * "not-ready": el render todavía no arrancó (el caller reintenta).
   */
  async captureSceneThumbnail(
    scene: ISceneDB,
  ): Promise<"saved" | "not-ready" | "failed"> {
    if (!this.LivePreview) return "not-ready";

    const canvas = await this.LivePreview.snapshotScene(
      scene,
      SCENE_THUMB_WIDTH,
      SCENE_THUMB_HEIGHT,
    );

    if (!canvas) return "not-ready";

    const bytes = await canvasToJpegBytes(canvas);

    if (!bytes) return "failed";

    return (await writeSceneThumbnail(scene.model.id, bytes)) ? "saved" : "failed";
  }

  /** Precarga el asset de un stinger (lo llama la UI tras subirlo). */

  async prewarmStinger(cfg: StingerConfig) {
    if (!this.LivePreview) return;

    await this.LivePreview.prewarmStinger(cfg);
  }

  /** True si el LivePreview (pipeline de render Pixi) ya está creado. */

  hasLivePreview(): boolean {
    return !!this.LivePreview;
  }

  /**
   * Crea el LivePreview + arranca el render SOLO si todavía no existe.
   * Idempotente. Sirve para "paint-on-load": en F5, si el gate de initializers
   * no llegó a disparar startScene (race con la init de las cámaras), creamos el
   * pipeline en cuanto una fuente inicializa (desde WebcamPreview.refreshPixi).
   */

  async ensureScene() {
    if (this.LivePreview) return;

    if (!this.ScenesManager.getModel()) return;

    await this.startScene();
  }

  destroyPlayers() {
    if (!this.LivePreview) return;

    this.LivePreview.destroyPlayers();
  }

  /**
   * @desc Updates Model
   * - resorces, sizings, positions
   * `persist: false` aplica el modelo solo en memoria (el OSR recibe el modelo que el control ya
   * guardó; escribirlo otra vez desde ahí duplicaba la escritura en Dexie en cada edición).
   */

  async updateTemplate(template: IStudio, options?: { persist?: boolean }) {
    try {
      if (options?.persist === false) {
        this.ScenesManager.setModelInMemory(template);
      } else {
        await this.ScenesManager.setModel(template);
      }
    } catch (error) {
      console.error(error);
    }

    if (!this.LivePreview) return;

    try {
      await this.LivePreview.updateModel(template);
    } catch (error) {
      console.error(error);
    }
  }

  async deleteArea(area: IStudioArea) {
    if (!this.LivePreview) return;

    this.LivePreview.deleteArea(area.id);
  }

  private async startPlaylist() {
    const db = await openDatabase();

    const playlist = await db.playlist.toArray();

    await this.Playlist.set(playlist);
  }

  private async initConfigurations() {
    const configuration = await CurrentWorkspace.getConfig();

    const userLang = await CurrentUser.getLang();

    if (configuration?.configuration?.outputDevice) {
      // Fire-and-forget: setOutputContext ya es defensivo (no-op si el contexto

      // se cerró, captura errores), así que nunca escala a unhandledrejection.

      void this.GlobalAudio.setOutputContext(
        configuration.configuration.outputDevice as MediaDeviceInfo,
      );
    }

    if (userLang) {
      i18n.changeLanguage(userLang);
    }
  }

  async stop() {
    // Control window: detener el broadcast del OSR window (engine allá).

    if (!isBroadcastWindow()) {
      await stopOsrBroadcastFromControl();

      return;
    }

    await this.Broadcast.stop(this.LivePreview);
  }

  async destroy() {
    try {
      this.unregisterDeviceChangeReseed();

      this.GlobalAudio.destroy();

      if (this.LivePreview) {
        if (this.LivePreview.updateOnLive) this.LivePreview.updateOnLive(false);

        this.LivePreview.stop()

          .catch((error) => {
            console.error("❌ Radio - Error stopping LivePreview:", error);
          })

          .finally(() => {
            if (!this.LivePreview) return;

            this.LivePreview.destroyPlayers();
          });
      }

      this.ScenesManager.destroy();

      SceneManager.destroy();

      this.Microphones.destroy();

      this.Playlist.destroy();
    } catch (error) {
      console.error(error);
    } finally {
      this.SystemAudio?.destroy();

      this.Streaming.stop();

      this.Cameras.destroy();

      this.ScenesManager.destroy();

      this.Triggers?.destroy();

      GuestAudioManager.destroy();

      this.State = States.destroyed;

      RadioInstance = null;
    }
  }

  async start() {
    // El engine de broadcast vive en el OSR window. Desde el control window delegamos:

    // abrir el OSR (renderiza + NVENC encodea). El broadcast real corre dentro del OSR.

    if (!isBroadcastWindow()) {
      await startOsrBroadcastFromControl();

      return;
    }

    try {
      // Defensive guard for startup races: ensure LivePreview exists before mergeStream.

      if (!this.LivePreview) {
        await this.startScene();
      }

      await this.Broadcast.start(
        this.LivePreview,

        this.GlobalAudio,

        this.startScene.bind(this),
      );
    } catch (error) {
      console.error("❌ Error in record process:", error);

      throw error;
    }
  }

  /**
   * Salud actual del broadcast (encoder del OSR + salidas RTMP en LOCAL; transporte en
   * cloud/selfhost). BroadcastComposition la publica cada 1s al control window, que la muestra
   * en el title bar (BroadcastHealth).
   */

  getConnectionStats(): BroadcastConnectionStats {
    return this.Streaming.getBroadcastStats();
  }

  onTrigger(action: string, handler: (payload: unknown) => void) {
    return this.Triggers.onAction(action, handler);
  }

  async updateMicrophone(oldMic: ITeamDeviceDB, newMic: ITeamDeviceDB) {
    const oldDeviceId = oldMic.deviceInfo.deviceId;

    // Solo re-capturamos si el mic viejo estaba al aire: cambiar de dispositivo
    // con el mic apagado no debe prenderlo.
    const wasActive =
      this.Microphones.getActiveMicrophones().includes(oldDeviceId);

    if (wasActive) {
      await this.Microphones.stop(oldDeviceId);
    }

    this.Microphones.delete(oldDeviceId);

    this.Microphones.set(newMic.deviceInfo.deviceId, newMic);

    if (wasActive) {
      await this.Microphones.play(
        newMic.deviceInfo.deviceId,
        newMic.capabilities,
      );
    }

    setTimeout(() => {
      window.postMessage({ type: "team.update" });
    }, 2000);
  }
}

export default Radio;
